import { FC, useMemo, useState } from "react";
import { Box } from "@mui/material";
import type { GridApi } from "ag-grid-community";
import { useAppSelector } from "@/store/hooks";
import { ResourceType } from "@/services/cloudchipr.api";
import { ResourcesMasterGrid } from "@/components/pages/common/resources-ag-grid/ResourcesMasterGrid";
import { selectedResourcesByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/selectedResourcesByMasterIdSelector";
import { AllTypeResourcesData } from "@/store/resources-grid/utils/types/resources";
import { resourcesMasterGridIdByResourceTypeSelector } from "@/store/resources-grid/selectors/grid/resourcesMasterGridIdByResourceTypeSelector";
import {
  resourceDetailGridPadding,
  resourceGridRowHeight,
} from "@/components/pages/common/resources-ag-grid/utils/constants/sizes";

export interface CreateIntegrationResourcesTableProps {
  resourceType: ResourceType;
}

export const CreateIntegrationResourcesTable: FC<
  CreateIntegrationResourcesTableProps
> = ({ resourceType }) => {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const masterGridId = useAppSelector((state) =>
    resourcesMasterGridIdByResourceTypeSelector(state, resourceType),
  );

  const selectedResources = useAppSelector((state) =>
    selectedResourcesByMasterIdSelector(state, masterGridId ?? ""),
  );

  const gridData = useMemo(() => {
    return selectedResources?.slice(0, 25)?.map(
      (resource) =>
        ({
          resource,
          id: resource?.id,
          resource_type: resource?.resource_type,
          child_resource: null,
        }) as AllTypeResourcesData,
    );
  }, [selectedResources]);

  if (!gridData || !masterGridId) {
    return null;
  }

  const height =
    gridData?.length * resourceGridRowHeight +
    resourceGridRowHeight +
    resourceDetailGridPadding;

  return (
    <Box height={height} maxHeight={300}>
      <ResourcesMasterGrid
        hideExpand
        hideSelection
        hideRowActions
        gridApi={gridApi}
        setGridApi={setGridApi}
        preventDetailsDrawerOpener
        resourceType={resourceType}
        overrideGridData={gridData}
        masterResourceType={resourceType}
        agGridsWrapperStyles={agGridsWrapperStyles}
        masterGridId={`${resourceType}-slack-message-master-grid-id`}
      />
    </Box>
  );
};

const agGridsWrapperStyles = {
  border: 1,
  borderColor: "grey.300",
  borderRadius: 2,
  overflow: "hidden",
};
